// CODICE api\facility-crud.ts

import type { ImpiantiPaginationDTO } from '@/models/dto/impianto/impianti-pagination-dto'
import type { ImpiantoDTO } from '@/models/dto/impianto/impianto-dto'
import type { ReportImpiantoDTO } from '@/models/dto/report/report-impianto-dto'
import type { ReportImpiantoPaginationDTO } from '@/models/dto/report/report-impianto-pagination-dto'
import { mapToFacilities } from '@/models/mappers/facility/facilities-mapper'
import { mapToFacility, mapToImpiantoDTO, mapToNuovoImpiantoDTO } from '@/models/mappers/facility/facility-mapper'
import { mapToFacilityReport } from '@/models/mappers/report/facility-report-mapper'
import { mapToFacilityReports } from '@/models/mappers/report/facility-reports-mapper'
import type { FacilitiesWithPagination } from '@/models/types/facility/facilities-with-pagination'
import type { Facility, NewFacility } from '@/models/types/facility/facility'
import type { FacilityReport } from '@/models/types/report/facility-report'
import type { FacilityReportsWithPagination } from '@/models/types/report/facility-reports-with-pagination'
import type { ReportType } from '@/models/types/report/report-type'
import type { OrderType } from '@/types/table'
import { capitalizeFirstLetter } from '@/utils/utils'
// import { FACILITY_URL } from '@/config/env'
import type { FetchOptionsType } from './api'
import { deleteJsonRequest, getRequest, postJsonRequest, putJsonRequest } from './make-request'

type FacilityGetOptions = {
    order?: OrderType
    sort?: keyof ImpiantoDTO
    search?: string
    page?: string
} & Partial<Record<keyof Facility, string>>

type FacilityReportsGetOptions = {
    order?: OrderType
    sort?: keyof ReportImpiantoDTO
    search?: string
    page?: string
}

type FacilityCrudType = (fetchOptions?: FetchOptionsType) => {
    getOne: (id: number) => Promise<Facility>
    getAll: () => Promise<Facility[]>
    getMany: (
        itemsPerPage?: string,
        options?: FacilityGetOptions,
        onlyEnabled?: boolean,
    ) => Promise<FacilitiesWithPagination>
    post: (facility: NewFacility) => Promise<Facility>
    put: (facility: Facility, confirmIfcOverwrite?: boolean) => Promise<Facility>
    delete: (id: number) => Promise<void>
    getManyReports: (
        itemsPerPage?: string,
        options?: FacilityReportsGetOptions,
    ) => Promise<FacilityReportsWithPagination>
    generateReport: (
        type: ReportType,
        facilityId: number,
        datetimeFrom: string,
        datetimeTo: string,
    ) => Promise<FacilityReport>
    deleteReport: (id: number) => Promise<void>
}

const FACILITY_URL =
    typeof window === 'undefined' ? `${process.env.API_URL}/api/v1/Impianti` : `${location.origin}/api/v1/Impianti`
console.log('FACILITY_URL:', FACILITY_URL)
const FACILITY_TAG = 'facility'

const facilityCRUD: FacilityCrudType = (fetchOptions) => ({
    getOne: async (id) => {
        
        const url = new URL(`${FACILITY_URL}/${id.toString()}`)
        console.log('[URL ONE]',url)
        const impiantiDTO = await getRequest<ImpiantoDTO>({ url, next: { tags: [FACILITY_TAG] }, ...fetchOptions })
        const facility = mapToFacility(impiantiDTO)
        return facility
    },
    getAll: async () => {
        
        const url = new URL(FACILITY_URL)
        console.log('[URL ALL]',url)
        const impiantiDTOs = await getRequest<ImpiantoDTO[]>({ url, next: { tags: [FACILITY_TAG] }, ...fetchOptions })
        const facilities = impiantiDTOs.map((impiantoDTO) => mapToFacility(impiantoDTO))
        return facilities
    },
    getMany: async (itemsPerPage, options, onlyEnabled) => {
        console.log('[URL MANY]')
        const url = new URL(`${FACILITY_URL}/Pagination`)
        url.searchParams.append('PageSize', itemsPerPage ?? '999999')
        options?.page && url.searchParams.append('PageIndex', options.page)
        options?.order && url.searchParams.append('Descending', options.order === 'desc' ? 'true' : 'false')
        options?.sort && url.searchParams.append('SortBy', capitalizeFirstLetter(options.sort))

        onlyEnabled && url.searchParams.append('OnlyActive', 'true')

        options?.search && url.searchParams.append('Filters[search]', options.search)

        options?.name && url.searchParams.append('Filters[Nome]', options.name)
        options?.address && url.searchParams.append('Filters[Indirizzo]', options.address)
        options?.city && url.searchParams.append('Filters[Città]', options.city)
        options?.province && url.searchParams.append('Filters[Provincia]', options.province)
        options?.postalCode && url.searchParams.append('Filters[Cap]', options.postalCode)
        options?.enabled && url.searchParams.append('Filter[Attivo]', options.enabled)
        options?.latitude && url.searchParams.append('Filters[Latitudine]', options.latitude)
        options?.longitude && url.searchParams.append('Filters[Longitudine]', options.longitude)
        options?.description && url.searchParams.append('Filters[Descrizione]', options.description)
        options?.status && url.searchParams.append('Filters[StatoImpianto]', options.status)
        console.log('[crud getMany] url', url.toString())
        const impiantiPaginationDTO = await getRequest<ImpiantiPaginationDTO>({
            url,
            next: { tags: [FACILITY_TAG] },
            ...fetchOptions,
        })
        const impiantiPagination = mapToFacilities(impiantiPaginationDTO)
        return impiantiPagination
    },
    post: async (facility) => {
        const newImpiantiDTO = mapToNuovoImpiantoDTO(facility)
        const url = new URL(FACILITY_URL)
        const impiantiDTOResponse = await postJsonRequest<ImpiantoDTO>({
            url,
            next: { tags: [FACILITY_TAG] },
            body: newImpiantiDTO,
            ...fetchOptions,
        })
        const newFacility = mapToFacility(impiantiDTOResponse)
        return newFacility
    },
    put: async (facility, confirmIfcOverwrite) => {
        const impiantiDTO = { ...mapToImpiantoDTO(facility), confirmIfcOverwrite: confirmIfcOverwrite ?? false }
        const url = new URL(FACILITY_URL)
        const impiantiDTOResponse = await putJsonRequest<ImpiantoDTO>({
            url,
            next: { tags: [FACILITY_TAG] },
            body: impiantiDTO,
            ...fetchOptions,
        })
        const newFacility = mapToFacility(impiantiDTOResponse)
        return newFacility
    },
    delete: async (id) => {
        const url = new URL(`${FACILITY_URL}/${id.toString()}`)
        console.log('[crud delete] url', url.toString())
        await deleteJsonRequest({ url, next: { tags: [FACILITY_TAG] }, ...fetchOptions })
    },
    getManyReports: async (itemsPerPage, options) => {
        const url = new URL(`${FACILITY_URL}/Report/Pagination`)
        url.searchParams.append('PageSize', itemsPerPage ?? '999999')
        options?.page && url.searchParams.append('PageIndex', options.page)
        options?.order && url.searchParams.append('Descending', options.order === 'desc' ? 'true' : 'false')
        options?.sort && url.searchParams.append('SortBy', capitalizeFirstLetter(options.sort))

        options?.search && url.searchParams.append('Filters[search]', options.search)

        const reportImpiantoPaginationDTO = await getRequest<ReportImpiantoPaginationDTO>({
            url,
            next: { tags: [FACILITY_TAG] },
            ...fetchOptions,
        })
        const facilityReports = mapToFacilityReports(reportImpiantoPaginationDTO)
        return facilityReports
    },
    generateReport: async (type, facilityId, datetimeFrom, datetimeTo) => {
        const url = new URL(`${FACILITY_URL}/Report`)
        const body = {
            tipologia: type,
            idImpianto: facilityId,
            datetimeFrom,
            datetimeTo,
        }
        const reportImpiantoDTO = await postJsonRequest<ReportImpiantoDTO>({
            url,
            next: { tags: [FACILITY_TAG] },
            headers: {
                'Content-Type': 'application/json-patch+json',
                Accept: 'application/json',
            },
            body,
            ...fetchOptions,
        })
        const report = mapToFacilityReport(reportImpiantoDTO)
        return report
    },
    deleteReport: async (id) => {
        const url = new URL(`${FACILITY_URL}/Report/${id.toString()}`)
        await deleteJsonRequest({ url, next: { tags: [FACILITY_TAG] }, ...fetchOptions })
    },
})

export { FACILITY_TAG, facilityCRUD, type FacilityCrudType, type FacilityGetOptions }
